import { NotFoundError } from 'core/infra/errors/NotFoundError'
import { isToday } from 'core/utils/date'

import { sentry } from 'legacy/utils/sentry'

import { shouldSuggestOnBid } from '../domain/LaneHistory'
import { getPreferredLaneKey, type PreferredLane } from '../domain/PreferredLane'
import { type LaneHistoryRepository } from '../infra/LaneHistoryRepository'
import { type PreferredLaneRepository } from '../infra/PreferredLaneRepository'
import { type SuggestedPreferredLaneRepository } from '../infra/SuggestedPreferredLaneRepository'

export type ShouldSuggestPreferredLaneOnBid = (lane: PreferredLane) => Promise<boolean>

export type ShouldSuggestPreferredLaneOnBidDependencies = {
  laneHistoryRepository: LaneHistoryRepository
  preferredLaneRepository: PreferredLaneRepository
  suggestedPreferredLaneRepository: SuggestedPreferredLaneRepository
}

export function shouldSuggestPreferredLaneOnBidFactory({
  laneHistoryRepository,
  preferredLaneRepository,
  suggestedPreferredLaneRepository,
}: ShouldSuggestPreferredLaneOnBidDependencies): ShouldSuggestPreferredLaneOnBid {
  return async function shouldSuggestPreferredLaneOnBid(
    preferredLane: PreferredLane
  ): Promise<boolean> {
    try {
      const key = getPreferredLaneKey(preferredLane)
      const laneHistory = await laneHistoryRepository.getById(key)

      if (laneHistory == null) {
        return false
      }

      const suggestions = await suggestedPreferredLaneRepository.getAll()
      const suggestionsToday = suggestions.filter(suggestion => isToday(suggestion.suggestionDate))

      // Should not suggest if there was already 3 suggestions today
      if (suggestionsToday.length >= 3) {
        return false
      }

      if (!shouldSuggestOnBid(laneHistory)) {
        return false
      }

      const retrievedPreferredLane = await preferredLaneRepository.getByPreferredLane(preferredLane)

      // Should not suggest if this lane is already a preferred lane
      if (retrievedPreferredLane != null) {
        return false
      }

      return true
    } catch (error) {
      // If a matching preferred lane was not found, we should suggest
      if (error instanceof NotFoundError) {
        return true
      }

      sentry.log(error)
      return false
    }
  }
}
